import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageTitle } from '../../hooks/usePageTitle';

const PHONE_DISPLAY = '(954) 296-4896';
const PHONE_DIAL = '+19542964896';
const WHATSAPP = 'https://wa.me/19542964896';
const ORG_ID = 'https://floridaaimarketingexperts.com/#organization';

export interface LocationData {
  slug: string;
  city: string;
  metro: string;            // "Miami-Dade County"
  headline: string;
  subhead: string;
  population: string;       // "2.7 million"
  neighborhoods: string[];
  stormAngle: string;       // hurricane / storm-specific paragraph
  whyLocal: string[];       // 3 bullets: why local AI marketing matters here
  verticals: string[];
  faqs: { q: string; a: string }[];
  lat: number;
  lng: number;
}

export default function LocationPage({ data }: { data: LocationData }) {
  const url = `https://floridaaimarketingexperts.com/${data.slug}`;

  usePageTitle({
    title: `AI Marketing in ${data.city}, FL | ChatGPT Ads, GEO & Voice AI for Contractors`,
    description: `${data.city} home-service contractors: get recommended by ChatGPT, run profitable OpenAI ads, and capture every storm lead with 24/7 Voice AI. Serving ${data.metro}. Free AI marketing audit.`,
    keywords: `AI marketing ${data.city}, ChatGPT ads ${data.city}, GEO ${data.city}, voice AI contractors ${data.city}, ${data.city} roofing marketing, AI marketing Florida contractors`,
    canonical: url,
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${url}#service`,
        name: `AI Marketing for ${data.city} Home-Service Contractors`,
        description: `ChatGPT Ads readiness, Generative Engine Optimization, and 24/7 Voice AI lead capture for roofing, HVAC, and remodeling contractors in ${data.city}, Florida.`,
        url,
        serviceType: 'AI Marketing',
        provider: { '@id': ORG_ID },
        areaServed: { '@type': 'City', name: data.city, containedInPlace: { '@type': 'AdministrativeArea', name: data.metro } },
        offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', price: '0', priceCurrency: 'USD', description: 'Free AI Marketing Audit' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': url + '#localbusiness',
        name: `Florida AI Marketing Experts — ${data.city}`,
        url,
        telephone: PHONE_DIAL,
        parentOrganization: { '@id': ORG_ID },
        areaServed: { '@type': 'City', name: data.city },
        geo: { '@type': 'GeoCoordinates', latitude: data.lat, longitude: data.lng },
        priceRange: '$$',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://floridaaimarketingexperts.com/' },
          { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://floridaaimarketingexperts.com/service-areas' },
          { '@type': 'ListItem', position: 3, name: data.city, item: url },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://ugmsgrbawrdcehdgdjin.supabase.co/storage/v1/object/public/brand-assets/stock-images/hero-bg-001.jpg"
            alt={`AI marketing for ${data.city} contractors`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/85 via-navy-900/70 to-navy-900/85" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block bg-teal-50 border border-teal-200 text-orange-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Serving {data.metro}
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {data.headline}
            </h1>
            <p className="text-lg md:text-2xl mb-8 text-navy-100 leading-relaxed max-w-3xl mx-auto">
              {data.subhead}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/audit"
                className="btn-primary px-8 py-4 text-lg"
              >
                Get Your Free AI Marketing Audit
              </Link>
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 mb-6">
            AI Marketing Built for {data.city} Home-Service Contractors
          </h2>
          <p className="text-lg text-navy-600 leading-relaxed mb-6">
            {data.city} is a {data.population}-resident market across {data.metro}, and the way homeowners
            find a roofer, HVAC tech, or remodeler has already changed. Before they ever open Google Maps,
            they ask ChatGPT, Perplexity, or Gemini: <em>"who's the best roofer near {data.neighborhoods[0]}?"</em>
            If the AI doesn't know your business exists, you never make the shortlist — no matter how good your
            Google reviews are.
          </p>
          <p className="text-lg text-navy-600 leading-relaxed">
            Florida AI Marketing Experts is an AI-first agency. We make sure ChatGPT recommends you (Generative
            Engine Optimization), we build profitable OpenAI / ChatGPT-era ad funnels, and we deploy 24/7 Voice AI
            so no {data.city} lead ever hits voicemail again.
          </p>
        </div>
      </section>

      {/* Storm angle */}
      <section className="py-16 bg-navy-50">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-2xl p-8 border-2 border-navy-100 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-hail-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-navy-900 mb-3">
                  When the Storm Hits {data.city}, the Phone Doesn't Stop
                </h3>
                <p className="text-navy-600 leading-relaxed">{data.stormAngle}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why local */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 mb-10 text-center">
            Why {data.city} Contractors Win With AI Marketing
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {data.whyLocal.map((point, i) => (
              <div key={i} className="bg-navy-50 rounded-xl p-6 border-2 border-navy-100 hover:border-teal-300 transition-all">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-check-line text-xl text-teal-600"></i>
                </div>
                <p className="text-navy-700 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services / internal links */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-10 text-center">
            What We Run for {data.city} Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/chatgpt-ads" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-6 transition-all block">
              <i className="ri-chat-3-line text-3xl text-orange-400 mb-3 block"></i>
              <h3 className="font-display text-xl font-bold mb-2">ChatGPT & OpenAI Ads</h3>
              <p className="text-navy-100 text-sm">Profitable, conversion-tracked ad funnels built for the AI-search era.</p>
            </Link>
            <Link to="/geo" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-6 transition-all block">
              <i className="ri-search-eye-line text-3xl text-orange-400 mb-3 block"></i>
              <h3 className="font-display text-xl font-bold mb-2">GEO — Get Recommended by AI</h3>
              <p className="text-navy-100 text-sm">Generative Engine Optimization so ChatGPT and Perplexity name you in {data.city}.</p>
            </Link>
            <Link to="/voice-ai" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-6 transition-all block">
              <i className="ri-customer-service-2-line text-3xl text-orange-400 mb-3 block"></i>
              <h3 className="font-display text-xl font-bold mb-2">24/7 Voice AI</h3>
              <p className="text-navy-100 text-sm">An AI receptionist that answers, qualifies, and books {data.city} jobs around the clock.</p>
            </Link>
          </div>
          <p className="text-center text-navy-100 mt-8">
            Industries we serve in {data.city}: {data.verticals.join(' · ')}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-900 mb-10 text-center">
            {data.city} AI Marketing FAQs
          </h2>
          <div className="space-y-4">
            {data.faqs.map((f, i) => (
              <details key={i} className="bg-navy-50 rounded-xl p-6 border border-navy-100 group">
                <summary className="font-semibold text-navy-900 cursor-pointer list-none flex justify-between items-center">
                  {f.q}
                  <i className="ri-add-line text-teal-600 group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="text-navy-600 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Own AI Search in {data.city}?
          </h2>
          <p className="text-lg text-navy-100 mb-8">
            Get a free AI Marketing Audit. We'll show whether ChatGPT recommends you today, where your leads leak,
            and exactly what to fix first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/audit"
              className="btn-primary px-8 py-4 text-lg"
            >
              Get Your Free AI Marketing Audit
            </Link>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-8 py-4 text-lg inline-flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Message {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
